import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

export default function ArticlePage() {
  const { _id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchArticle = async () => {
      try {
        const articleNumber = _id.split('-').pop();
        const response = await fetch(`https://tbido-website-api.azurewebsites.net/getArticlebyId/${articleNumber}`);

        if (!response.ok) {
          throw new Error('Article not found');
        }

        const data = await response.json();
        setArticle(data.article);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching article:', error);
        setLoading(false);
      }
    };

    fetchArticle();
  }, [_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-gray-600 mb-4">Article not found</p>

        </div>
      </div>
    );
  }

  const getCategoryColor = (categoryName) => {
    const colors = [
      'bg-red-100 text-red-800',
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800',
      'bg-purple-100 text-purple-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800',
      'bg-teal-100 text-teal-800',
      'bg-orange-100 text-orange-800',
      'bg-cyan-100 text-cyan-800',
      'bg-rose-100 text-rose-800'
    ];

    let hash = 0;
    for (let i = 0; i < categoryName.length; i++) {
      hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const renderContent = () => {
    return article.articleContent.map((block, index) => (
      <React.Fragment key={index}>
        {/* Render the header if it exists */}
        {block.contentHeader && (
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 mb-3">
            {block.contentHeader}
          </h3>
        )}
        {/* Render the description if it exists */}
        {block.contentDescription && (
          <div 
            className="text-sm sm:text-base text-gray-700 mb-4 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: block.contentDescription }}
          />
        )}
      </React.Fragment>
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="relative w-full h-[250px] sm:h-[350px] md:h-[400px] lg:h-[500px] overflow-hidden">
        <img
          src={article.articleImage}
          alt={article.articleTitle}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        <div className="flex flex-row absolute top-4 sm:top-6 left-4 sm:left-6">
          {article.articleSDG.map((sdg, index) => (
            <img 
              key={index}
              src={`/images/SDGs/SDG${sdg}.jpg`} 
              alt="UNDP" 
              className="h-8 sm:h-10 pr-2 md:h-12 w-auto"
            />
          ))}
        </div>


      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        <div className="mb-6 sm:mb-8">
          
          <nav className="flex flex-wrap items-center text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
            <Link to="/" className="hover:text-[#6D0C22] transition-colors font-medium">
              Home
            </Link>
            <span className="mx-2 text-gray-400">/</span>
            <button 
              onClick={() => navigate(-1)} 
              className="hover:text-[#6D0C22] transition-colors font-medium"
            >
              Blogs
            </button>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-800 font-semibold truncate max-w-[150px] sm:max-w-[300px] md:max-w-[450px]">
              {article.articleTitle}
            </span>
          </nav>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-5 md:mb-6">
            {article.articleTitle}
          </h1>
          
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 border-l-4 border-red-900 pl-3 sm:pl-4">
            <div>
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Written by:</p>
              <p className="font-semibold text-gray-900">{article.articleAuthor}</p>
            </div>
            <span className="text-gray-400 hidden sm:inline">•</span>
            <div>
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Published on:</p>
              <p className="font-semibold text-gray-900">{new Date(article.articleDate).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
          {article.articleCategory.map((category, index) => (
            <span 
              key={index}
              className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold ${getCategoryColor(category)}`}
            >
              {category}
            </span>
          ))}
        </div>

        <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">
          <p className="text-sm sm:text-base text-gray-700 mb-4 leading-relaxed">
            {article.articleDescription}
          </p>
          {renderContent()}
        </div>

        <div className="mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8 border-t-2 border-gray-200">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Share this article</h3>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm">
              Facebook
            </button>
            <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors text-xs sm:text-sm">
              Twitter
            </button>
            <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm">
              WhatsApp
            </button>
          </div>
        </div>

        <div className="mt-10 sm:mt-12 md:mt-16">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Related Articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/blogs')}>
              <h4 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">More articles coming soon...</h4>
              <p className="text-xs sm:text-sm text-gray-600">Stay tuned for more startup stories and innovation insights.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}